/**
 * "Add a term" / "Propose the term" — V104-G-12 (ADR-0077 §5–§6).
 *
 * An inline form that files a PENDING jurisdiction term: the law's own word
 * for who a duty falls on (a role) or for a risk tier, what it counts as in
 * the fixed set, the section that defines it and its source. The term can be
 * picked at once; a rule that uses it applies only after a reviewer approves
 * the term in the Editorial queue. The jurisdiction is the law's (the server
 * takes it from the item), so it is shown, not asked.
 */
import { useEffect, useId, useState } from 'react';
import { Alert, Button, Field, Input, Select, Textarea } from '@aigovops/design-system';
import { CANONICAL_ROLES, CANONICAL_ROLE_LABEL, CANONICAL_TIER_LABEL, RULE_TIERS, type JurisdictionTerm } from '@aigovops/core';
import { ApiError, proposeItemTerm } from '../lib/api';
import { toDutyId } from '../lib/rule-authoring';

export interface TermProposalFormProps {
  itemId: string;
  /** Shown as context only ("Colorado"); the server uses the item's law. */
  jurisdictionLabel: string;
  kind: 'role' | 'tier';
  /** Let the editor switch between a role and a risk-tier word (the needs-authoring card). */
  chooseKind?: boolean;
  /** Prefill: a word the drafter used that the jurisdiction has not declared. */
  defaultWord?: string;
  defaultSourceUrl?: string | null;
  /** The jurisdiction's current tier words, to suggest a rank. */
  existing?: readonly JurisdictionTerm[];
  onFiled: (term: JurisdictionTerm) => void;
  onCancel: () => void;
  /** When the word already exists for the jurisdiction: pick it instead of filing a duplicate. */
  onUseExisting?: (term: JurisdictionTerm) => void;
  /** Prefill for "Section that defines it" (e.g. the duty's own section). */
  defaultCitation?: string;
}

/** Server field (validator prefix) → this form's field key. */
const SERVER_FIELD: Record<string, string> = {
  term_key: 'label', label: 'label', canonical: 'canonical', rank: 'rank', citation: 'citation', source_url: 'source', definition: 'quote',
};
/** Validator text → plain words for the field (never keys or ADR numbers). */
function plainServerText(field: string, text: string, what: string): string {
  if (/already declares|already waiting/.test(text)) return 'This word is already declared or waiting for review.';
  if (/retired key|deprecated/.test(text)) return 'This word was retired. Use a different word.';
  if (/lower tier|share rank/.test(text)) return 'Its strictness clashes with another tier word. Pick a different number.';
  switch (field) {
    case 'label': return 'Write the word as the law uses it (letters and numbers).';
    case 'canonical': return `Choose what this ${what} counts as.`;
    case 'rank': return 'Use a whole number from 1 to 20; higher is stricter.';
    case 'citation': return 'Add the section that defines the word.';
    case 'source': return 'Add a link to the law (http or https).';
    default: return 'Check this field.';
  }
}

export function TermProposalForm({ itemId, jurisdictionLabel, kind: initialKind, chooseKind, defaultWord, defaultSourceUrl, defaultCitation, existing = [], onFiled, onCancel, onUseExisting }: TermProposalFormProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const [kind, setKind] = useState<'role' | 'tier'>(initialKind);
  const tiers = existing.filter((t) => t.kind === 'tier');
  const nextRank = tiers.reduce((m, t) => Math.max(m, t.rank ?? 0), 0) + 1;
  const [label, setLabel] = useState(defaultWord ? defaultWord.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '');
  const [canonical, setCanonical] = useState('');
  const [rank, setRank] = useState(String(nextRank));
  const [citation, setCitation] = useState(defaultCitation ?? '');
  const [sourceUrl, setSourceUrl] = useState(defaultSourceUrl ?? '');
  const [quote, setQuote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const what = kind === 'role' ? 'role' : 'risk tier';
  const id = (k: string) => `term-${uid}-${k}`;
  // Focus moves after the render that shows the target (and its error), never in the same tick.
  const [focusTarget, setFocusTarget] = useState<{ key: string } | null>({ key: chooseKind && !defaultWord ? 'kind' : 'label' });
  useEffect(() => {
    if (!focusTarget) return;
    document.getElementById(id(focusTarget.key))?.focus();
    setFocusTarget(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTarget]);

  const termKey = toDutyId(defaultWord && toDutyId(defaultWord) && label.trim().toLowerCase() === defaultWord.replace(/_/g, ' ').toLowerCase() ? defaultWord : label);
  // The same word already exists (or waits for review) for this jurisdiction: offer it instead of a duplicate.
  const clash = existing.find((t) => t.kind === kind && t.term_key === termKey && t.status !== 'deprecated');

  async function file() {
    if (busy) return;
    const e: Record<string, string> = {};
    if (label.trim().length < 2) e.label = 'Write the word as the law uses it.';
    else if (clash) e.label = `${jurisdictionLabel} already has “${clash.label}”${clash.status === 'pending' ? ' (waiting for review)' : ''}.`;
    if (!canonical) e.canonical = `Choose what this ${what} counts as.`;
    if (kind === 'tier' && !/^\d+$/.test(rank.trim())) e.rank = 'Use a whole number; higher is stricter.';
    if (!citation.trim()) e.citation = 'Add the section that defines the word.';
    if (!/^https?:\/\/\S+$/.test(sourceUrl.trim())) e.source = 'Add a link to the law (http or https).';
    setErrors(e);
    setServerErrors([]);
    const first = Object.keys(e)[0];
    if (first) {
      setFocusTarget({ key: first });
      return;
    }
    setBusy(true);
    try {
      const res = await proposeItemTerm(itemId, {
        kind,
        term_key: termKey,
        label: label.trim(),
        canonical,
        ...(kind === 'tier' ? { rank: Number(rank) } : {}),
        citation: citation.trim(),
        source_url: sourceUrl.trim(),
        ...(quote.trim() ? { quote: quote.trim(), definition: quote.trim() } : {}),
      });
      onFiled(res.term);
    } catch (err) {
      const body = (err instanceof ApiError ? err.body : null) as { errors?: string[]; issues?: string[]; detail?: string } | null;
      const list = [...(body?.errors ?? []), ...(body?.issues ?? [])];
      // Each server problem goes to its field, in plain words; anything else is one fixed sentence.
      const fieldErrors: Record<string, string> = {};
      let general = false;
      for (const raw of list) {
        const m = /^([a-z_]+)(?:\.[a-z_]+)?:\s*(.*)$/s.exec(raw);
        const field = m ? SERVER_FIELD[m[1]!] : undefined;
        if (field && !fieldErrors[field]) fieldErrors[field] = plainServerText(field, m![2]!, what);
        else general = true;
      }
      setErrors(fieldErrors);
      setServerErrors(general || !list.length ? ['The word wasn’t filed. Check the fields and try again.'] : []);
      const first = Object.keys(fieldErrors)[0];
      setFocusTarget({ key: general || !first ? 'server' : first });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-labelledby={id('h')}
      // This form sits inside the rule form: Enter in one of its fields files the word, not the rule.
      onKeyDown={(e) => {
        const t = e.target as HTMLElement;
        if (e.key === 'Enter' && t.tagName === 'INPUT' && !e.nativeEvent.isComposing) {
          e.preventDefault();
          void file();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
        }
      }}
      style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface-elevated)', padding: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}
    >
      <h3 id={id('h')} style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
        Add a {what} word for {jurisdictionLabel}
      </h3>
      <div id={id('server')} tabIndex={-1} role="alert">
        {serverErrors.length ? <Alert variant="error">{serverErrors.join(' ')}</Alert> : null}
      </div>
      {clash && onUseExisting ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>
          <span>{jurisdictionLabel} already has “{clash.label}”.</span>
          <Button type="button" compact variant="secondary" onClick={() => onUseExisting(clash)}>
            Use “{clash.label}”
          </Button>
        </div>
      ) : null}
      <div style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))' }}>
        {chooseKind ? (
          <Field id={id('kind')} label="Kind of word">
            <Select
              value={kind}
              onChange={(e) => {
                setKind(e.target.value === 'tier' ? 'tier' : 'role');
                setCanonical('');
              }}
            >
              <option value="role">Who a duty falls on (role)</option>
              <option value="tier">Risk tier</option>
            </Select>
          </Field>
        ) : null}
        <Field id={id('label')} label="Word in the law" hint={kind === 'role' ? 'For example: Developer' : 'For example: High-risk AI system'} error={errors.label} errorRole="none" required>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <Field id={id('canonical')} label="Counts as" error={errors.canonical} errorRole="none" required>
          <Select value={canonical} onChange={(e) => setCanonical(e.target.value)}>
            <option value="">Choose…</option>
            {kind === 'role'
              ? CANONICAL_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {CANONICAL_ROLE_LABEL[r]}
                  </option>
                ))
              : RULE_TIERS.map((t) => (
                  <option key={t} value={t}>
                    {CANONICAL_TIER_LABEL[t]}
                  </option>
                ))}
          </Select>
        </Field>
        {kind === 'tier' ? (
          <Field id={id('rank')} label="Strictness in this law" hint="Higher is stricter. It can’t count as a lower tier than a less strict word." error={errors.rank} errorRole="none" required>
            <Input type="number" min={1} max={20} step={1} inputMode="numeric" value={rank} onChange={(e) => setRank(e.target.value)} />
          </Field>
        ) : null}
        <Field id={id('citation')} label="Section that defines it" hint="For example: C.R.S. 6-1-1701" error={errors.citation} errorRole="none" required>
          <Input value={citation} onChange={(e) => setCitation(e.target.value)} />
        </Field>
      </div>
      <Field id={id('source')} label="Link to the law" error={errors.source} errorRole="none" required>
        <Input type="url" value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} />
      </Field>
      <Field id={id('quote')} label="Definition from the law (optional)">
        <Textarea rows={2} value={quote} onChange={(e) => setQuote(e.target.value)} />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <Button type="button" compact variant="primary" onClick={() => void file()} aria-disabled={busy || undefined}>
          {busy ? 'Filing…' : 'File for review'}
        </Button>
        <Button type="button" compact variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  );
}
